import logo from 'public:images/sky-logo.webp';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router';
import { Button } from 'softcat:components/button.tsx';
import { ErrorMessage } from 'softcat:components/error-message.tsx';
import { LanguageSelector } from 'softcat:components/language-selector.tsx';
import { AuthConfig } from 'softcat:config/auth.ts';
import { SsoConfig } from 'softcat:config/sso.ts';
import { useAuth as useAppAuth } from 'softcat:hooks/use-auth.ts';
import { EnvironmentUtils } from 'softcat:utils/environment.ts';

/**
 * If running in localhost, you have to add a role to your user in AWS Cognito to be able to log in.
 * Steps:
 * 1. Log in the app with your Sky SSO credentials. This creates a user in AWS Cognito.
 * 2. Find your username:
 *  aws cognito-idp list-users --user-pool-id eu-west-1_mJDIiEVp4 --filter 'email = "<first_lastname>@comcast.com"'
 * 3. Add the role to your user:
 * aws cognito-idp admin-add-user-to-group --region eu-west-1
 *   --user-pool-id <cognito_user_pool_id>
 *   --username <your_username>
 *   --group-name SUPERADMIN
 */
export const SignIn = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [ssoError] = useState((location.state as { ssoError?: string })?.ssoError ?? '');
    const { isAuthenticated, refreshUser, useCognitoLogin } = useAppAuth();
    const oidcAuth = useAuth();
    const stage = EnvironmentUtils.getStageName(globalThis.location.hostname);

    useEffect(() => {
        if (location.state?.ssoError) {
            globalThis.history.replaceState({}, '');
        }
    }, [location.state]);

    const onSsoSignIn = () => {
        if (SsoConfig.USE_COGNITO_LOGIN) {
            oidcAuth.signinRedirect();
            return;
        }

        oidcAuth.signinRedirect({
            extraQueryParams: {
                identity_provider: SsoConfig.SKY_SSO_PROVIDER_NAME,
            },
        });
    };

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/apps');
        } else if (localStorage.getItem(AuthConfig.AccessTokenStorageKey)) {
            refreshUser()
                .then(() => {
                    navigate('/apps');
                })
                .catch(() => {
                    localStorage.removeItem(AuthConfig.AccessTokenStorageKey);
                });
        }
    }, [isAuthenticated, navigate, refreshUser]);

    return (
        <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-28 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <img alt="Sky Q" className="mx-auto h-36 w-auto" src={logo} />

                <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                    <FormattedMessage id="SIGN_IN_PAGE_HEADING" />
                </h2>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <div className="mb-8 space-y-3 px-4 [&>button]:w-full [&>button]:sm:w-full">
                    <Button onClick={onSsoSignIn} style={'submit'}>
                        <FormattedMessage
                            id={
                                useCognitoLogin
                                    ? 'SIGN_IN_PAGE_COGNITO_BUTTON'
                                    : 'SIGN_IN_PAGE_SSO_BUTTON'
                            }
                        />
                    </Button>

                    {ssoError === AuthConfig.AuthErrors.INSUFFICIENT_PERMISSIONS && (
                        <div className="flex justify-center">
                            <ErrorMessage>
                                <FormattedMessage
                                    id={'SIGN_IN_PAGE_SSO_INSUFFICIENT_PERMISSIONS'}
                                />
                            </ErrorMessage>
                        </div>
                    )}

                    {ssoError === AuthConfig.AuthErrors.UPLOADER_ROLE && (
                        <div className="flex justify-center">
                            <ErrorMessage>
                                <FormattedMessage id={'SIGN_IN_PAGE_FORM_DENY_UPLOADER'} />
                            </ErrorMessage>
                        </div>
                    )}
                </div>

                {useCognitoLogin && (
                    <div className="flex justify-center flex-col items-center gap-2 align-middle">
                        <span>
                            Run this command to find your username and password. Make sure
                            you&lsquo;re connected to the dev AWS account
                        </span>
                        <code>
                            aws ssm get-parameter --region eu-west-1 --with-decryption --name
                            /keystone/softcat/{stage}/ci/integration-test-user --query
                            Parameter.Value --output text
                        </code>
                    </div>
                )}

                <div className={'py-12 text-center'}>
                    <LanguageSelector anchor={'bottom'} showLabel={true} />
                </div>
            </div>
        </div>
    );
};
